import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Truck, UserCheck, Package } from "lucide-react";
import api from "../lib/api";
import { Modal, Badge, Button, PageLoader } from "../lib/ui";
import { ORDER_STATUS, fmtNum, orderLabel, orderOwner } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { usePrintDoc } from "../lib/usePrintDoc";

const FF_LABEL = { PICKING: "Picking", PACKING: "Packing", READY: "READY", LOADED: "LOADED", IN_TRANSIT: "IN_TRANSIT" };
const dt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Track({ label, value }) {
  return (
    <div>
      <p className="font-semibold uppercase tracking-wide text-ink-mute">{label}</p>
      <p className="text-ink">{value || "—"}</p>
    </div>
  );
}

export default function OrderDetailModal({ orderId, onClose }) {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { printDoc, printing } = usePrintDoc();
  const [o, setO] = useState(null);

  useEffect(() => {
    if (!orderId) { setO(null); return; }
    setO(null);
    api.get(`/orders/${orderId}`).then(({ data }) => setO(data)).catch(() => { toast.error(t("Failed")); onClose(); });
  }, [orderId]); // eslint-disable-line

  const doc = (kind, label) => printDoc(`/orders/${o.id}/${kind}`, { key: `${kind}-${o.id}`, label });

  const owner = o && orderOwner(o);
  const timeline = o ? [
    ["Created", o.created_at, o.created_by_name],
    ["Submitted to Central", o.submitted_at],
    ["Approved", o.approved_at],
    ["Purchased", o.purchased_at],
    [o.assigned_driver_name ? `${t("Assigned to")} ${o.assigned_driver_name}` : "Assigned", o.assigned_at],
    ["Delivered", o.delivered_at],
    ["Received", o.received_at],
  ].filter((r) => r[1]) : [];

  return (
    <Modal open={!!orderId} onClose={onClose} title={o ? orderLabel(o) : t("Order")} size="lg"
      footer={o && o.status === "DRAFT" && (user?.role === "BRANCH_STAFF" || user?.role === "SUPER_ADMIN") && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => { onClose(); navigate(`/orders/${o.id}/edit`); }} data-testid="odm-edit-draft">{t("Edit Draft")}</Button>
        </div>
      )}>
      {!o ? <PageLoader /> : (
        <div data-testid="order-detail">
          {["DELIVERED", "RECEIVED"].includes(o.status) && (
            <div className="mb-3 flex flex-wrap gap-2 rounded-xl border border-primary/20 bg-primary-light/40 p-3" data-testid="odm-doc-actions">
              <Button loading={printing === `surat-jalan-${o.id}`} onClick={() => doc("surat-jalan", t("Surat Jalan"))} data-testid="odm-surat-jalan"><FileText size={16} /> {t("Print Delivery Note")}</Button>
              <Button variant="outline" loading={printing === `invoice-${o.id}`} onClick={() => doc("invoice", t("Invoice"))} data-testid="odm-invoice"><FileText size={16} /> {t("Invoice")}</Button>
            </div>
          )}
          <div className="mb-3 rounded-xl border border-line bg-app/60 p-4" data-testid="order-detail-tracking">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <p className="font-heading text-lg font-bold text-ink">{orderLabel(o)}</p>
              <span className="font-mono text-xs font-semibold text-ink-mute">{o.order_no}</span>
              <Badge tone={ORDER_STATUS[o.status]?.tone || "mute"}>{t(ORDER_STATUS[o.status]?.label || o.status)}</Badge>
              {o.status === "PURCHASED" && o.fulfillment_status && <Badge tone="info">{t(FF_LABEL[o.fulfillment_status] || o.fulfillment_status)}</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-3">
              <Track label={t("Branch")} value={o.branch_name} />
              <Track label={t("Order Date")} value={o.order_date} />
              <Track label={t("Requested Delivery")} value={o.requested_delivery_date ? `${o.requested_delivery_date}${o.requested_delivery_time ? " " + o.requested_delivery_time : ""}` : "—"} />
              <div>
                <p className="font-semibold uppercase tracking-wide text-ink-mute">{t("Current Owner")}</p>
                {owner ? <Badge tone={owner.tone} className="mt-0.5"><Package size={11} /> {t(owner.label)}</Badge> : <p className="text-ink">—</p>}
              </div>
              {o.assigned_driver_name && (
                <div><p className="font-semibold uppercase tracking-wide text-ink-mute">{t("Driver")}</p>
                  <span className="inline-flex items-center gap-1 text-ink"><UserCheck size={13} className="text-emerald-600" /> {o.assigned_driver_name}</span></div>
              )}
              <Track label={t("Created By")} value={o.created_by_name} />
            </div>
            {o.notes && <p className="mt-3 border-t border-line pt-2 text-xs text-ink-soft"><span className="font-semibold uppercase text-ink-mute">{t("Notes")}: </span>{o.notes}</p>}
          </div>

          {/* Items */}
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="py-2">{t("Product")}</th>
                <th className="py-2 text-right">{t("Ordered")}</th>
                <th className="py-2 text-right">{t("Delivered")}</th>
                <th className="py-2 text-center">{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {o.items?.map((it, i) => (
                <tr key={i} className="border-b border-line/60">
                  <td className="py-2 font-medium">{it.product_name} {it.is_additional && <Badge tone="info" className="ml-1">{t("+ added")}</Badge>}</td>
                  <td className="py-2 text-right">{fmtNum(it.ordered_quantity)} {it.unit}</td>
                  <td className="py-2 text-right">{fmtNum(it.delivered_quantity)} {it.unit}</td>
                  <td className="py-2 text-center"><span className="text-xs">{t(it.checklist_status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="space-y-2 sm:hidden" data-testid="order-detail-items-mobile">
            {o.items?.map((it, i) => (
              <div key={i} className="rounded-xl border border-line bg-white p-3">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <p className="min-w-0 break-words text-sm font-semibold text-ink">{it.product_name}</p>
                  <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-600">{t(it.checklist_status)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><p className="font-semibold uppercase tracking-wide text-ink-mute">{t("Ordered")}</p><p className="text-ink">{fmtNum(it.ordered_quantity)} {it.unit}</p></div>
                  <div><p className="font-semibold uppercase tracking-wide text-ink-mute">{t("Delivered")}</p><p className="text-ink">{fmtNum(it.delivered_quantity)} {it.unit}</p></div>
                </div>
              </div>
            ))}
          </div>

          {/* History timeline */}
          {timeline.length > 0 && (
            <div className="mt-4 border-t border-line pt-3" data-testid="order-detail-history">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("History")}</p>
              <ol className="space-y-2">
                {timeline.map(([label, when, who], i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <div><span className="font-semibold text-ink">{t(label)}</span>
                      <span className="text-ink-mute"> · {dt(when)}{who ? ` · ${who}` : ""}</span></div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
